// src/lib/supabase/queries/sprint-queries.ts
/**
 * .tuMetrics {"tu1": 0.85, "tu2": 0.87, "tu3": 5, "notes": "Sprint DB mappers and CRUD queries", "tuVersion": "2.5"}
 * .help "Supabase queries and row mappers for sprints (fetch, insert, update, delete, task linking)."
 */
import { supabase } from '../../supabase';
import { Sprint, SentimentVotes } from '../../../types';

// July 2026: types/supabase.ts (generated Database types) is an empty file, so
// the old `Database['public']['Tables']['sprints']` lookups never type-checked.
// Hand-written insert/update shapes until codegen is wired up.
type SprintInsert = {
  name: string;
  start_date: string;
  end_date: string;
  status: Sprint['status'];
  sentiment_votes: SentimentVotes | string | null;
  github_milestone_id: string | null;
  team_id: string | null;
  goals: string | null;
  company_id: string | null;
  project_id: string | null;
  created_by_id: string | null;
  requested_by_id: string | null;
};

type SprintUpdate = Partial<SprintInsert> & {
  status_reason?: string | null;
  paused_at?: string | null;
  paused_reason?: string | null;
  paused_by_id?: string | null;
  resumed_at?: string | null;
  resumed_by_id?: string | null;
};

/* --------------------------- helpers & mappers --------------------------- */

export const parseSentimentVotes = (sentimentVotes: any): SentimentVotes => {
  if (!sentimentVotes) return { up: 0, down: 0, user_votes: [] };

  const parsed =
    typeof sentimentVotes === 'string' ? JSON.parse(sentimentVotes) : sentimentVotes;

  // New format
  if (parsed && typeof parsed === 'object' && 'user_votes' in parsed) {
    return {
      up: Number(parsed.up) || 0,
      down: Number(parsed.down) || 0,
      user_votes: Array.isArray(parsed.user_votes) ? parsed.user_votes : [],
    };
  }

  // Old format fallback
  if (parsed && typeof parsed === 'object' && 'votedBy' in parsed) {
    return {
      up: Number(parsed.up) || 0,
      down: Number(parsed.down) || 0,
      user_votes: Array.isArray(parsed.votedBy)
        ? parsed.votedBy.map((userId: string) => ({ user_id: userId, type: 'up' as const }))
        : [],
    };
  }

  return { up: 0, down: 0, user_votes: [] };
};

export const mapSprintFromDB = (row: any, tasks?: string[]): Sprint => ({
  id: row.id,
  name: row.name || 'Unnamed Sprint',
  startDate: row.start_date,
  endDate: row.end_date,
  status: (row.status || 'planned') as Sprint['status'],
  tasks: tasks || [],
  sentimentVotes: parseSentimentVotes(row.sentiment_votes),
  githubMilestone: row.github_milestone_id ? JSON.parse(row.github_milestone_id) : undefined,
  teamId: row.team_id || undefined,
  goals: row.goals || undefined,
  companyId: row.company_id || undefined,
  roadmapId: row.roadmap_id || undefined,
  projectId: row.project_id || undefined, // November 3, 2025: Added projectId mapping
  createdById: row.created_by_id || undefined,
  requestedById: row.requested_by_id || undefined,
  
  // Timestamps
  createdAt: row.created_at || undefined,
  updatedAt: row.updated_at || undefined,
  
  // Status tracking
  statusReason: row.status_reason || undefined,
  statusChangedAt: row.status_changed_at || undefined,
  statusChangedById: row.status_changed_by_id || undefined,
  
  // Pause/Resume tracking
  pausedAt: row.paused_at || undefined,
  pausedReason: row.paused_reason || undefined,
  pausedById: row.paused_by_id || undefined,
  resumedAt: row.resumed_at || undefined,
  resumedById: row.resumed_by_id || undefined,
});

export const mapSprintToDB = (sprint: Omit<Sprint, 'id' | 'tasks'>): SprintInsert => ({
  name: sprint.name,
  start_date: sprint.startDate,
  end_date: sprint.endDate,
  status: sprint.status,
  // For sprints.sentiment_votes (jsonb) we pass a JSON object
  sentiment_votes: sprint.sentimentVotes || { up: 0, down: 0, user_votes: [] },
  // Column stores a JSON string in your schema; keep consistent with current behavior
  github_milestone_id: sprint.githubMilestone ? JSON.stringify(sprint.githubMilestone) : null,
  team_id: sprint.teamId || null,
  goals: sprint.goals || null,
  company_id: sprint.companyId || null,
  project_id: sprint.projectId || null, // November 3, 2025: Added project_id mapping
  created_by_id: sprint.createdById || null,
  requested_by_id: sprint.requestedById || null,
});

export const mapSprintUpdatesToDB = (updates: Partial<Sprint>): SprintUpdate => {
  const dbUpdates: SprintUpdate = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.startDate !== undefined) dbUpdates.start_date = updates.startDate;
  if (updates.endDate !== undefined) dbUpdates.end_date = updates.endDate;
  if (updates.status !== undefined) dbUpdates.status = updates.status;
  if (updates.statusReason !== undefined) dbUpdates.status_reason = updates.statusReason || null;
  if (updates.githubMilestone !== undefined)
    dbUpdates.github_milestone_id = updates.githubMilestone
      ? JSON.stringify(updates.githubMilestone)
      : null;
  if (updates.sentimentVotes !== undefined)
    dbUpdates.sentiment_votes = updates.sentimentVotes;
  if (updates.teamId !== undefined) dbUpdates.team_id = updates.teamId || null;
  if (updates.goals !== undefined) dbUpdates.goals = updates.goals || null;
  if (updates.companyId !== undefined) dbUpdates.company_id = updates.companyId || null;
  if (updates.projectId !== undefined) dbUpdates.project_id = updates.projectId || null; // November 3, 2025: Added projectId update mapping
  if (updates.createdById !== undefined) dbUpdates.created_by_id = updates.createdById || null;
  if (updates.requestedById !== undefined)
    dbUpdates.requested_by_id = updates.requestedById || null;
  
  // Pause/Resume fields
  if (updates.pausedAt !== undefined) dbUpdates.paused_at = updates.pausedAt || null;
  if (updates.pausedReason !== undefined) dbUpdates.paused_reason = updates.pausedReason || null;
  if (updates.pausedById !== undefined) dbUpdates.paused_by_id = updates.pausedById || null;
  if (updates.resumedAt !== undefined) dbUpdates.resumed_at = updates.resumedAt || null;
  if (updates.resumedById !== undefined) dbUpdates.resumed_by_id = updates.resumedById || null;
  
  return dbUpdates;
};

/* --------------------------- supabase operations --------------------------- */

export const fetchSprints = async (companyId: string, selectedTeamId: string | null) => {
  let query = supabase.from('sprints').select('*').eq('company_id', companyId);

  if (selectedTeamId !== null) {
    query = query.eq('team_id', selectedTeamId);
  }

  // Newest first — matches other work inventories (cards, projects, roadmaps, meetings)
  query = query.order('created_at', { ascending: false }).order('start_date', { ascending: false });

  const { data: sprintsData, error: sprintsError } = await query;
  if (sprintsError) throw sprintsError;

  // Load tasks (cards) mapped to these sprints, scoping by company
  const { data: cardsData, error: cardsError } = await supabase
    .from('cards')
    .select('id, sprint_id')
    .eq('company_id', companyId)
    .eq('type', 'task');

  if (cardsError) throw cardsError;

  const sprintCards = (cardsData || []).reduce((acc, card) => {
    if (card.sprint_id) {
      if (!acc[card.sprint_id]) acc[card.sprint_id] = [];
      acc[card.sprint_id].push(card.id);
    }
    return acc;
  }, {} as Record<string, string[]>);

  const sprints = (sprintsData || []).map((sprint) => ({
    ...mapSprintFromDB(sprint, sprintCards[sprint.id]),
  }));

  return { sprints };
};

export const insertSprint = async (sprintData: SprintInsert) => {
  const { data, error } = await supabase
    .from('sprints')
    .insert(sprintData)
    .select('*')
    .single();
  if (error) throw error;
  return data;
};

export const updateSprintById = async (
  sprintId: string,
  updates: Partial<Sprint>,
  tasks?: string[]
) => {
  const dbUpdates = mapSprintUpdatesToDB(updates);

  // Update the sprint row and return the updated record
  const { data: updatedSprintRow, error: sprintError } = await supabase
    .from('sprints')
    .update(dbUpdates)
    .eq('id', sprintId)
    .select('*')
    .single();

  if (sprintError) throw sprintError;

  // Optionally update tasks assigned to this sprint
  // December 2, 2025: Use stored procedure for safe sprint linking (bypasses RLS)
  if (tasks !== undefined) {
    // Get all cards currently in this sprint
    const { data: currentCards, error: fetchError } = await supabase
      .from('cards')
      .select('id')
      .eq('sprint_id', sprintId)
      .eq('company_id', updatedSprintRow.company_id);

    if (fetchError) throw fetchError;

    const currentCardIds = (currentCards || []).map(c => c.id);
    
    // Unlink cards that were removed from sprint
    const cardsToUnlink = currentCardIds.filter(id => !tasks.includes(id));
    for (const cardId of cardsToUnlink) {
      const { error: unlinkError } = await supabase.rpc('link_card_to_sprint', {
        p_card_id: cardId,
        p_sprint_id: null
      });
      if (unlinkError) throw unlinkError;
    }

    // Link new cards to sprint
    if (tasks.length > 0) {
      const { data: results, error: linkError } = await supabase.rpc('link_cards_to_sprint', {
        p_card_ids: tasks,
        p_sprint_id: sprintId
      });
      
      if (linkError) throw linkError;
      
      // Check for any failures in bulk operation
      const failures = (results || []).filter((r: { success: boolean }) => !r.success);
      if (failures.length > 0) {
        console.error('Failed to link some cards:', failures);
        throw new Error(`Failed to link ${failures.length} card(s) to sprint`);
      }
    }
  }

  return updatedSprintRow;
};

export const deleteSprintById = async (sprintId: string) => {
  const t0 = performance.now();
  console.log('[Sprint delete] API: deleteSprintById start', { sprintId });

  // Clear task associations first
  // December 2, 2025: Use stored procedure for safe unlinking
  const { data: cardsInSprint, error: fetchError } = await supabase
    .from('cards')
    .select('id')
    .eq('sprint_id', sprintId);

  if (fetchError) {
    console.error('[Sprint delete] API: fetch cards in sprint failed', { sprintId, error: fetchError.message });
    throw fetchError;
  }

  const cardIds = (cardsInSprint || []).map((c) => c.id);
  const cardCount = cardIds.length;
  console.log('[Sprint delete] API: cards in sprint', { sprintId, cardCount, cardIds: cardCount <= 10 ? cardIds : `${cardCount} cards` });

  // Unlink all cards in one bulk RPC (was: 45 sequential calls ~10s; now: 1 call)
  if (cardCount > 0) {
    const { data: unlinkResults, error: unlinkError } = await supabase.rpc('link_cards_to_sprint', {
      p_card_ids: cardIds,
      p_sprint_id: null,
    });
    if (unlinkError) {
      console.error('[Sprint delete] API: bulk unlink failed', { sprintId, error: unlinkError.message });
      throw unlinkError;
    }
    const failed = (unlinkResults || []).filter((r: { success: boolean }) => r.success === false);
    if (failed.length > 0) {
      const first = failed[0] as { card_id: string; error_message: string };
      console.error('[Sprint delete] API: some cards failed to unlink', { sprintId, failedCount: failed.length, firstError: first?.error_message });
      throw new Error(`Failed to unlink ${failed.length} card(s) from sprint: ${first?.error_message ?? 'unknown'}`);
    }
    console.log('[Sprint delete] API: all cards unlinked', { sprintId, cardCount, ms: Math.round(performance.now() - t0) });
  }

  const { error } = await supabase.from('sprints').delete().eq('id', sprintId);
  if (error) {
    console.error('[Sprint delete] API: sprint row delete failed', { sprintId, error: error.message });
    throw error;
  }
  console.log('[Sprint delete] API: deleteSprintById done', { sprintId, ms: Math.round(performance.now() - t0) });
  return true;
};

// Included to satisfy exports used by callers; currently not utilized.
export const getUserVote = (_sprintId: string, _userId: string) => {
  return null;
};
